import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

/**
 * AuthCallback (Phase 11.2)
 *
 * Lands here from the backend's Entra round-trip:
 *
 *   GET /api/auth/{internal|external}/callback → 302 to
 *   /auth/callback?code=<one-time-code>
 *
 * Consumes the code by POSTing it to /api/auth/validate (via
 * finalizeFromCode in useAuth), which writes the access_token +
 * hydrated user into localStorage and then navigates to /chat.
 *
 * DOA alignment v2.2: the wire field is `code`, not `app_code`.
 *
 * React StrictMode double-mount guard: in dev, effects run twice.
 * The one-time app code can only be consumed once — a second call
 * returns 401 and would bounce the user back to /login with an
 * "invalid or expired code" error. The useRef latch ensures the
 * code is consumed exactly once.
 */
const AuthCallback: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { finalizeFromCode } = useAuth();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const error = params.get('error');
    if (error) {
      navigate(`/login?error=${encodeURIComponent(error)}`, { replace: true });
      return;
    }

    const code = params.get('code');
    if (!code) {
      navigate('/login?error=missing_code', { replace: true });
      return;
    }

    finalizeFromCode(code)
      .then(() => navigate('/chat', { replace: true }))
      .catch((e: any) => {
        const msg = e?.response?.data?.detail || 'validation_failed';
        navigate(`/login?error=${encodeURIComponent(msg)}`, { replace: true });
      });
  }, [params, navigate, finalizeFromCode]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="flex flex-col items-center gap-3 text-center">
        <Loader2
          className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400"
          aria-hidden="true"
        />
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
          Signing you in...
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
